import { type ReactNode, useState, useEffect, useCallback } from 'react';
import type { GameState } from '@bangos/shared';
import { socketManager } from '../socket-manager';
import { useT, useTranslateSpectrum } from '../i18n';
import '../styles/gameplay.css';

interface RevealData {
  targetPosition: number;
  guessPosition: number;
  distance: number;
  points: number;
  team: 'blue' | 'red';
  guesserId: string;
}

interface RevealScreenProps {
  gameState: GameState;
  revealData: RevealData | null;
  submittedClue: { id: string; playerId: string; text: string; position: number } | null;
  playerId: string;
}

export function RevealScreen({ gameState, revealData, submittedClue, playerId }: RevealScreenProps): ReactNode {
  const t = useT();
  const tSpectrum = useTranslateSpectrum();
  const { phase, currentRound } = gameState;
  const translatedSpectrum = currentRound?.spectrum ? tSpectrum(currentRound.spectrum) : null;

  const [isReadyForNext, setIsReadyForNext] = useState(false);
  const [readyPlayerIds, setReadyPlayerIds] = useState<Set<string>>(new Set());

  const totalActivePlayers = gameState.teams.blue.length + gameState.teams.red.length;
  const readyCount = readyPlayerIds.size;

  // Reset ready state on round changes and game over
  useEffect(() => {
    setIsReadyForNext(false);
    setReadyPlayerIds(new Set());
  }, [gameState.round, phase]);

  // Listen for other players signaling ready
  useEffect(() => {
    const handler = (payload: { playerId: string }) => {
      setReadyPlayerIds((prev) => {
        const next = new Set(prev);
        next.add(payload.playerId);
        return next;
      });
    };
    socketManager.on('player_ready_for_next_round', handler);
    return () => {
      socketManager.off('player_ready_for_next_round', handler);
    };
  }, []);

  // Reset on round_started event
  useEffect(() => {
    const handler = () => {
      setIsReadyForNext(false);
      setReadyPlayerIds(new Set());
    };
    socketManager.on('round_started', handler);
    return () => {
      socketManager.off('round_started', handler);
    };
  }, []);

  // Reset on game_over event
  useEffect(() => {
    const handler = () => {
      setIsReadyForNext(false);
      setReadyPlayerIds(new Set());
    };
    socketManager.on('game_over', handler);
    return () => {
      socketManager.off('game_over', handler);
    };
  }, []);

  const handleReadyClick = useCallback(() => {
    setIsReadyForNext(true);
    // Add self to local ready set so the count updates immediately
    setReadyPlayerIds((prev) => {
      const next = new Set(prev);
      next.add(playerId);
      return next;
    });
    socketManager.emit('ready_for_next_round', { roomCode: gameState.roomCode });
  }, [playerId, gameState.roomCode]);

  if (phase !== 'reveal' || !currentRound) return null;

  return (
    <div className="reveal-container">
      <h2 className="reveal-title">{t('reveal.title')}</h2>

      <div className="reveal-ready-section">
        {!isReadyForNext ? (
          <button className="btn btn--primary btn--ready-next" onClick={handleReadyClick}>
            {t('reveal.readyForNext')}
          </button>
        ) : (
          <div className="reveal-waiting-text">{t('reveal.waitingForOthers')}</div>
        )}
        <div className="reveal-ready-count">
          {t('reveal.readyCount', { ready: readyCount, total: totalActivePlayers })}
        </div>
      </div>

      <div className="spectrum-card">
        <div className="spectrum-title">{t('reveal.spectrumTitle')}</div>
        <div className="spectrum-concepts">
          <span className="spectrum-concept--left">{translatedSpectrum ? translatedSpectrum.left : currentRound.spectrum.left}</span>
          <span className="spectrum-concept--right">{translatedSpectrum ? translatedSpectrum.right : currentRound.spectrum.right}</span>
        </div>
      </div>

      {submittedClue && (
        <div className="clue-section">
          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#888', marginBottom: '0.3rem' }}>
            {t('reveal.clue', { text: submittedClue.text })}
          </div>
        </div>
      )}

      <div className="reveal-track">
        <div className="guess-spectrum-labels">
          <span className="guess-label--left">{translatedSpectrum ? translatedSpectrum.left : currentRound.spectrum.left}</span>
          <span className="guess-label--right">{translatedSpectrum ? translatedSpectrum.right : currentRound.spectrum.right}</span>
        </div>
        <div className="reveal-track-bar">
          {revealData && (
            <>
              <div
                className="reveal-marker-guess"
                style={{ left: `${revealData.guessPosition}%` }}
                title={`Guess: ${revealData.guessPosition}`}
              />
              <div
                className="reveal-marker-target"
                style={{ left: `${revealData.targetPosition}%` }}
                title={`Target: ${revealData.targetPosition}`}
              />
            </>
          )}
          {!revealData && currentRound.guesses.length > 0 && currentRound.targetPosition !== undefined && (
            <>
              <div
                className="reveal-marker-guess"
                style={{ left: `${currentRound.guesses[0].position}%` }}
                title={`Guess: ${currentRound.guesses[0].position}`}
              />
              <div
                className="reveal-marker-target"
                style={{ left: `${currentRound.targetPosition}%` }}
                title={`Target: ${currentRound.targetPosition}`}
              />
            </>
          )}
        </div>
      </div>

      <div className="reveal-score-card">
        <div className="reveal-score-row">
          <span className="reveal-score-label">{t('reveal.yourGuess')}</span>
          <span className="reveal-score-value">
            {revealData?.guessPosition ?? currentRound.guesses[0]?.position ?? '?'}
          </span>
        </div>
        <div className="reveal-score-row">
          <span className="reveal-score-label">{t('reveal.targetPosition')}</span>
          <span className="reveal-score-value">
            {revealData?.targetPosition ?? currentRound.targetPosition ?? '?'}
          </span>
        </div>
        <div className="reveal-score-row">
          <span className="reveal-score-label">{t('reveal.distance')}</span>
          <span className="reveal-score-value">
            {revealData?.distance ?? currentRound.score?.distance ?? '?'}
          </span>
        </div>
        <div className="reveal-score-row reveal-score-row-divider">
          <span className="reveal-score-label" style={{ fontWeight: 800 }}>{t('reveal.pointsEarned')}</span>
          <span className="reveal-score-value reveal-score-value-large">
            {revealData?.points ?? currentRound.score?.points ?? 0}
          </span>
        </div>
      </div>
    </div>
  );
}
